import type { SiteSocialNetworkKey } from './socialLinks';

/** Utility Bar chip kinds — values resolve from Local Presence / PDL at render. */
export const UTILITY_ITEM_KINDS = ['social', 'phone', 'email', 'address'] as const;
export type UtilityItemKind = (typeof UTILITY_ITEM_KINDS)[number];

export const UTILITY_MAX_ITEMS = 8;

export const UTILITY_DEFAULT_ICONS: Record<Exclude<UtilityItemKind, 'social'>, string> = {
  phone: 'Phone',
  email: 'Mail',
  address: 'MapPin',
};

export interface UtilityItemRow {
  id: string;
  kind: UtilityItemKind;
  /** Lucide key for phone / email / address. */
  icon?: string;
  /**
   * Legacy per-network social rows (ignored at resolve — one Social entry
   * expands to all Local Presence / custom social links).
   */
  network?: SiteSocialNetworkKey;
  /** Optional display label override for phone / email / address. */
  label?: string;
}

function isUtilityItemKind(value: unknown): value is UtilityItemKind {
  return (
    typeof value === 'string' &&
    (UTILITY_ITEM_KINDS as readonly string[]).includes(value)
  );
}

function normalizeRow(raw: unknown): UtilityItemRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (!isUtilityItemKind(row.kind)) return null;
  const id =
    typeof row.id === 'string' && row.id.trim()
      ? row.id.trim()
      : `item-${Math.random().toString(36).slice(2, 10)}`;
  const label =
    typeof row.label === 'string' && row.label.trim() ? row.label.trim() : undefined;
  if (row.kind === 'social') {
    // Network / label are unused — Value binds to all social links.
    return { id, kind: 'social' };
  }
  const icon =
    typeof row.icon === 'string' && row.icon.trim()
      ? row.icon.trim()
      : UTILITY_DEFAULT_ICONS[row.kind];
  return { id, kind: row.kind, icon, label };
}

/** Reads Utility Bar item rows from a locale content string (JSON array). */
export function readUtilityItems(raw: string): UtilityItemRow[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeRow)
      .filter((row): row is UtilityItemRow => row != null)
      .slice(0, UTILITY_MAX_ITEMS);
  } catch {
    return [];
  }
}

export function writeUtilityItems(rows: readonly UtilityItemRow[]): string {
  const normalized = rows
    .map((row) => normalizeRow(row))
    .filter((row): row is UtilityItemRow => row != null)
    .slice(0, UTILITY_MAX_ITEMS)
    .map((row) => {
      if (row.kind === 'social') {
        return { id: row.id, kind: row.kind };
      }
      return {
        id: row.id,
        kind: row.kind,
        icon: row.icon ?? UTILITY_DEFAULT_ICONS[row.kind],
        ...(row.label ? { label: row.label } : {}),
      };
    });
  return JSON.stringify(normalized);
}

export function createUtilityItemId(): string {
  return `util-${Math.random().toString(36).slice(2, 10)}`;
}

/** Default seed: social → phone → email → address. */
export function defaultUtilityItemRows(): UtilityItemRow[] {
  return [
    { id: createUtilityItemId(), kind: 'social' },
    { id: createUtilityItemId(), kind: 'phone', icon: UTILITY_DEFAULT_ICONS.phone },
    { id: createUtilityItemId(), kind: 'email', icon: UTILITY_DEFAULT_ICONS.email },
    { id: createUtilityItemId(), kind: 'address', icon: UTILITY_DEFAULT_ICONS.address },
  ];
}

export function parseUtilityDismissible(raw: string | undefined): boolean {
  return (raw ?? '').trim().toLowerCase() === 'true';
}

export function writeUtilityDismissible(value: boolean): string {
  return value ? 'true' : 'false';
}

export function parseUtilityRotateMs(raw: string | undefined): number {
  const n = Number.parseInt((raw ?? '').trim(), 10);
  if (!Number.isFinite(n) || n < 2000) return 4000;
  return Math.min(n, 20000);
}
